import {Box, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import './JobResultDataGrid.scss';

const renderCheckboxCell = (
  params: any,
  columnKey: string,
  isChecked: boolean,
  onCellChange: (rowId: number, columnKey: string, checked: boolean) => void,
  disabled: boolean,
  onCountClick: (rowId: number, isRelevant: boolean, count: number) => void
) => {
  const handleChange = (checked: boolean) => {
    onCellChange(params.row.reviewJobIssueCodeID, columnKey, checked);
  };

  const count = params.value;
  const isRelevant = columnKey === 'relevantCount';
  const isClickable = count > 0;

  const handleCountClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!isClickable) return;
    onCountClick(params.row.reviewJobIssueCodeID, isRelevant, count);
  };
  return (
    <Box
      className={`checkbox-cell-content ${isChecked ? 'checkbox-selected' : ''}`}
    >
      <ArrCheckbox
        size="medium"
        checked={isChecked}
        disabled={disabled}
        onChange={handleChange}
        className="checkbox-container"
        aria-label={`${params.value}`}
      />
      <Typography
        className={`typo-text body3_size job-value ${isClickable ? 'job-value-link' : ''}`}
        data-testid={`issue-code-column-${columnKey}-${params.documentId}`}
        onClick={isClickable ? handleCountClick : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') handleCountClick(e);
              }
            : undefined
        }
        aria-label={
          isClickable
            ? `${t('ai.investigate.viewInDocList')} ${params.value}`
            : undefined
        }
        aria-hidden={!isClickable}
      >
        {params.value}
      </Typography>
    </Box>
  );
};

const HeaderCheckbox = ({
  label,
  checked,
  indeterminate,
  onChange,
  dataTestId,
  disabled,
}: {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: (checked: boolean) => void;
  dataTestId: string;
  disabled?: boolean;
}) => (
  <Box
    className="header-checkbox"
    data-testid={`isse-code-header-checkbox-wrapper`}
    onClick={(e) => e.stopPropagation()}
  >
    <ArrCheckbox
      size="medium"
      checked={checked}
      isIndeterminate={indeterminate}
      onChange={(checked) => onChange(checked)}
      disabled={disabled}
      className="checkbox-container"
      aria-label={`${label} ${t('common.arrDataGridPro.checkbox.selectAll')}`}
    />
    <Typography
      className="typo-text label2_size"
      data-testid={`issue-code-column-typography-label-${dataTestId}`}
      aria-hidden="true" // Prevent double-reading since aria-label covers it
    >
      {label}
    </Typography>
  </Box>
);

export const getColumns = (
  checkboxState: Record<string, boolean>,
  onCellChange: (rowId: number, columnKey: string, checked: boolean) => void,
  onHeaderChange: (columnKey: string, checked: boolean) => void,
  rows: any[],
  onCountClick: (rowId: number, isRelevant: boolean, count: number) => void
): GridColDef[] => [
  {
    field: 'reviewJobIssueCode',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.issueCode'),
    sortable: true,
    filterable: true,
    minWidth: 220,
    flex: 1,
    renderHeader: () => (
      <Typography
        className="typo-text label2_size"
        data-testid={`issue-code-column-issueCode`}
      >
        {t('ai.review.jobTypeDataGrid.issueCodeColumns.issueCode')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        data-testid={`issue-code-column-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text subhead5_size"
          data-testid={`issue-code-column-${params.id}`}
        >
          {params.value}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`issueCode`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'question',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.question'),
    sortable: true,
    filterable: true,
    minWidth: 630,
    flex: 1,
    renderHeader: () => (
      <Typography
        className="typo-text label2_size"
        data-testid={`issue-code-column-question`}
      >
        {t('ai.review.jobTypeDataGrid.issueCodeColumns.question')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        data-testid={`issue-code-column-question-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text body3_size"
          data-testid={`issue-code-column-question-${params.id}`}
        >
          {params.value}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`question`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'relevantCount',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.releventDocs'),
    width: 227,
    sortable: true,
    filterable: false,
    cellClassName: 'checkbox-cell',
    headerClassName: 'checkbox-header',
    renderHeader: () => {
      const selectableRows = rows.filter((r: any) => r?.relevantCount > 0);
      const keys = selectableRows.map(
        (r: any) => `${r.reviewJobIssueCodeID}-relevantCount`
      );
      const checkedCount = keys.filter((k) => checkboxState[k]).length;
      const label = t(
        'ai.review.jobTypeDataGrid.issueCodeColumns.releventDocs'
      );
      return (
        <HeaderCheckbox
          label={label}
          checked={checkedCount === keys.length && keys.length > 0}
          indeterminate={checkedCount > 0 && checkedCount < keys.length}
          onChange={(checked) => onHeaderChange('relevantCount', checked)}
          dataTestId={'relevant-docs'}
          disabled={keys.length === 0}
        />
      );
    },
    renderCell: (params) =>
      renderCheckboxCell(
        params,
        'relevantCount',
        !!checkboxState[`${params.row.reviewJobIssueCodeID}-relevantCount`],
        onCellChange,
        !params.value,
        onCountClick
      ),
  },
  {
    field: 'nonRelevantCount',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.notReleventDocs'),
    width: 227,
    sortable: true,
    filterable: false,
    cellClassName: 'checkbox-cell',
    headerClassName: 'checkbox-header last-column',
    renderHeader: () => {
      const selectableRows = rows.filter((r: any) => r.nonRelevantCount > 0);
      const keys = selectableRows.map(
        (r: any) => `${r.reviewJobIssueCodeID}-nonRelevantCount`
      );
      const checkedCount = keys.filter((k) => checkboxState[k]).length;
      const label = t(
        'ai.review.jobTypeDataGrid.issueCodeColumns.notReleventDocs'
      );

      return (
        <HeaderCheckbox
          label={label}
          checked={checkedCount === keys.length && keys.length > 0}
          indeterminate={checkedCount > 0 && checkedCount < keys.length}
          onChange={(checked) => onHeaderChange('nonRelevantCount', checked)}
          dataTestId={'non-relevant-docs'}
          disabled={keys.length === 0}
        />
      );
    },
    renderCell: (params) =>
      renderCheckboxCell(
        params,
        'nonRelevantCount',
        !!checkboxState[`${params.row.reviewJobIssueCodeID}-nonRelevantCount`],
        onCellChange,
        !params.value,
        onCountClick
      ),
  },
];
